import React, { useEffect, useState } from 'react';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from "react-i18next";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@material-ui/core";
import md5 from 'md5';

import AppInput from "../../../../components/Input/AppInput";
import { SYSTEM_ROLE } from "../../../../config/AppConfig";
import AppButton from "../../../../components/CustomButtons/AppButton";
import {
    fetchUserCoordinatorDetail,
    reduceCreateUserCoordinator,
    reduceUpdateUserCoordinator,
    fetchUserHospitalDetail,
    reduceCreateUserHospital,
    reduceUpdateUserHospital
} from "../../../../store/slices/clientUser";
import { getAuthInfo } from "../../../../store/slices/auth";
import AppConfig from "config/AppConfig";
const { PATTERNS } = AppConfig;

const UserForm = props => {
    const { isAdd = true } = props;
    const { t } = useTranslation();
    const history = useHistory();
    const { id } = useParams();
    const dispatch = useDispatch();
    const user = useSelector(getAuthInfo);
    const [submitting, setSubmitting] = useState(false);
    const [initializeData, setInitializeData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: ''
    });

    const _submit = (data) => {
        setSubmitting(true);
        if (isAdd) {
            const request = user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? reduceCreateUserHospital : reduceCreateUserCoordinator;
            dispatch(request({
                firstName: data.firstName,
                lastName: data.lastName,
                role: data.role,
                email: data.email.trim(),
                passwordHash: md5(data.password)
            }))
                .then(({ payload }) => {
                    setSubmitting(false);
                    payload && history.goBack();
                })
        } else {
            const request = user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? reduceUpdateUserHospital : reduceUpdateUserCoordinator;
            dispatch(request({
                firstName: data.firstName,
                lastName: data.lastName,
                id
            }))
                .then(() => {
                    setSubmitting(false);
                    _loadData();
                })
        }
    }

    const _loadData = () => {
        const request = user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? fetchUserHospitalDetail : fetchUserCoordinatorDetail;
        dispatch(request({ id }))
            .then(({ payload }) => {
                if (payload) {
                    const data = { ...initializeData };
                    Object.keys(initializeData)
                        .forEach(key => {
                            data[key] = payload[key] || "";
                        })
                    setInitializeData(data);
                }
            })
    }

    useEffect(() => {
        if (!isAdd) {
            _loadData();
        }
    }, [])
    return (
        <Formik
            initialValues={initializeData}
            validationSchema={Yup.object().shape({
                firstName: Yup.string()
                    .trim()
                    .required('validation.first_name.required')
                    .max(64, { message: 'validation.max_length', value: 64 }),
                lastName: Yup.string()
                    .trim()
                    .required('validation.last_name.required')
                    .max(64, { message: 'validation.max_length', value: 64 }),
                email: Yup.string().trim()
                    .required('validation.email.required')
                    .email('validation.email.invalid')
                    .max(255, { message: 'validation.max_length', value: 255})
                    .matches(PATTERNS.BLANK_SPACES, 'validation.blankSpaces'),
                password: isAdd ? Yup.string()
                    .trim()
                    .required('validation.password.required')
                    .min(8, { message: 'validation.min_length', value: 8 })
                    .max(32, { message: 'validation.max_length', value: 32 })
                    .matches(/[A-Z]/g, 'validation.password.uppercase')
                    .matches(/[a-z]/g, 'validation.password.lowercase')
                    .matches(/[0-9]/g, 'validation.password.number')
                    : Yup.string(),
                role: isAdd ? Yup.string()
                    .required('validation.role.required') : Yup.string()

            })}
            onSubmit={_submit}
            enableReinitialize
        >
            {({ handleSubmit }) => (
                <>
                    <Field
                        component={AppInput}
                        name="email"
                        label={t('label.email')}
                        required
                        disabled={!isAdd}
                    />
                    <Field
                        component={AppInput}
                        name="firstName"
                        label={t('label.first_name')}
                        required
                    />
                    <Field
                        component={AppInput}
                        name="lastName"
                        label={t('label.last_name')}
                        required
                    />
                    {isAdd && (
                        <>
                            <Field
                                component={AppInput}
                                name="password"
                                label={t('label.password')}
                                type="password"
                                required
                            />
                            <Field
                                component={AppInput}
                                name="role"
                                label={t('label.role')}
                                type="select"
                                required
                            >
                                <option value="">{t('label.select_a_role')}</option>
                                {user.role === SYSTEM_ROLE.HOSPITAL_ADMIN ? (
                                    <>
                                        <option value={SYSTEM_ROLE.HOSPITAL_ADMIN}>{t(`user_role.${SYSTEM_ROLE.HOSPITAL_ADMIN}`)}</option>
                                        <option value={SYSTEM_ROLE.HOSPITAL_OTHER}>{t(`user_role.${SYSTEM_ROLE.HOSPITAL_OTHER}`)}</option>
                                    </>
                                ) : (
                                    <>
                                        <option value={SYSTEM_ROLE.COORDINATOR_ADMIN}>{t(`user_role.${SYSTEM_ROLE.COORDINATOR_ADMIN}`)}</option>
                                        <option value={SYSTEM_ROLE.COORDINATOR_OTHER}>{t(`user_role.${SYSTEM_ROLE.COORDINATOR_OTHER}`)}</option>
                                    </>
                                )}
                            </Field>
                        </>
                    )}
                    <Box display="flex" justifyContent="flex-end" alignItems="center" mt={3}>
                        <AppButton
                            label={t('button.back')}
                            onClick={() => history.goBack()}
                            mode="outlined"
                            style={{
                                marginRight: '1rem'
                            }}
                        />
                        <AppButton
                            label={t('button.save')}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            disabled={submitting}
                        />
                    </Box>
                </>
            )}
        </Formik>
    )
}

UserForm.propTypes = {
    isAdd: PropTypes.bool
}

export default UserForm;
